/**
 * Reading the server's pool preview — labels, ordering, and the sentences that
 * explain what it could not calculate.
 *
 * The arithmetic is NOT here. `POST /products/pool/preview` returns every
 * figure and the formula behind it, produced by the same pass that will store
 * the results (`cgt-backend/src/products/pool-calculations.ts`). This module
 * only turns that answer into what the dialog says, which is the part worth
 * testing without a DOM — `vitest.config.ts` runs `.test.ts` under `src` in a
 * node environment, so a component test would silently never run.
 *
 * The sibling of `splitPreview.ts`, deliberately kept as its own module: a
 * pool preview answers a different question (what several bags contribute to
 * one) and carries warnings a split has no concept of.
 */

import type {
  LimsResultRef,
  PoolDoseRule,
  PoolPreviewMember,
  PoolPreviewRow,
  PoolPreviewUnavailable,
  PoolWarning,
} from '@/types/products'
import { LIMS_RESULT_LABELS, LIMS_RESULT_ORDER, type LimsResultKey } from '@/lib/limsResults'

/**
 * A result's label, preferring this app's wording over the server's.
 *
 * Both name the same keys, but labels are presentation and the two have
 * drifted apart before. The KEY is the contract; the server's label is the
 * fallback for one this build does not know about.
 */
export function limsLabelFor(ref: LimsResultRef): string {
  return LIMS_RESULT_LABELS[ref.key as LimsResultKey] ?? ref.label
}

/**
 * The rows in this app's reading order, matching the LIMS details dialog.
 *
 * The server answers in ITS canonical order. Both are defensible; agreeing
 * with the other screen that shows these numbers matters more.
 */
export function orderPoolRows(rows: PoolPreviewRow[]): PoolPreviewRow[] {
  const position = new Map(LIMS_RESULT_ORDER.map((key, i) => [key as string, i]))
  return [...rows].sort(
    (a, b) => (position.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (position.get(b.key) ?? Number.MAX_SAFE_INTEGER),
  )
}

/** Why the preview has nothing to show, in words an operator can act on. */
export function poolUnavailableMessage(reason: PoolPreviewUnavailable): string {
  switch (reason) {
    case 'too_few_members':
      return 'Select at least two products to see what pooling them would produce.'
    case 'no_member_volumes':
      return 'None of the selected products has a recorded volume, so no pooled figure can be calculated. Record a volume on each bag first.'
    case 'no_measurements':
      return 'None of the selected products has a lab measurement on its forms, so there is nothing to combine. Measurements are read from the fields placed from the Field Library entries with the dose system names (tnc_per_ml, cd34_per_mcl, cd34_pct, cd3_per_mcl, cd3_pct).'
  }
}

/**
 * How a member's dose was measured, for the contribution table.
 *
 * Worth showing because exactly one of the three rules runs per product and
 * nothing in the resulting number records which — a reviewer comparing two
 * members cannot otherwise tell that one was read off a cytometer and the
 * other inferred from a percentage.
 */
export function doseRuleLabel(rule: PoolDoseRule | null): string {
  switch (rule) {
    case 'direct':
      return 'Entered directly'
    case 'single':
      return 'Single platform'
    case 'dual':
      return 'Dual platform'
    default:
      return '—'
  }
}

/**
 * The Buffer added field, read strictly.
 *
 * Blank means no buffer, and so does 0. Anything else that is not a number of
 * mL, 0 or more, is an ERROR rather than "no buffer": the buffer divides every
 * pooled concentration, so silently dropping a typed `-5` would preview and
 * store a pool the operator did not describe.
 */
export function parseBufferMl(text: string): { value?: number; error?: string } {
  const trimmed = text.trim()
  if (trimmed === '') return {}
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return { error: 'Buffer must be a number of mL, 0 or more.' }
  return n > 0 ? { value: n } : {}
}

/** The warnings an operator must acknowledge before pooling, most serious first. */
export function blockingWarnings(warnings: PoolWarning[]): PoolWarning[] {
  return orderedWarnings(warnings, null).filter(warningIsBlocking)
}

/** Whether a warning should stop an operator rather than merely inform them. */
export function warningIsBlocking(warning: PoolWarning): boolean {
  return warning.code === 'zero_tnc' || warning.code === 'no_member_volumes'
}

/**
 * Warnings worth showing, most serious first.
 *
 * `no_measurements` is dropped when the preview is already reporting it as the
 * reason nothing can be shown — the same fact stated twice on one panel reads
 * as two problems.
 */
export function orderedWarnings(
  warnings: PoolWarning[],
  unavailable: PoolPreviewUnavailable | null,
): PoolWarning[] {
  const rank: Record<PoolWarning['code'], number> = {
    zero_tnc: 0,
    no_member_volumes: 1,
    direct_entry_override: 2,
    single_platform_lost: 3,
    no_measurements: 4,
  }
  return warnings
    .filter(w => !(unavailable !== null && (w.code as string) === (unavailable as string)))
    .sort((a, b) => rank[a.code] - rank[b.code])
}

/**
 * A figure as the panel prints it.
 *
 * Small values keep four significant digits because a CD3/kg dose is around
 * 0.0000873 and "0.00" would be a lie; large ones are trimmed, because float
 * noise is not precision. Matches the server's own `show`.
 */
export function formatPoolValue(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  if (Number.isInteger(value)) return String(value)
  const magnitude = Math.abs(value)
  if (magnitude >= 1000) return value.toFixed(1)
  if (magnitude >= 1) return String(Number(value.toFixed(4)))
  return String(Number(value.toPrecision(4)))
}

/** A member's line in the contribution table. */
export interface PoolMemberSummary {
  label: string
  volume: string
  tnc: string
  cd34: string
  cd3: string
  rule: string
}

/**
 * The contribution table, one row per member.
 *
 * A member with no volume still gets a row — it was selected, and silently
 * dropping it would leave an operator wondering which bag was ignored.
 */
export function poolMemberSummaries(members: PoolPreviewMember[]): PoolMemberSummary[] {
  return members.map((m, i) => ({
    label: m.label ?? `Product ${i + 1}`,
    volume: m.volumeMl === null ? 'No volume recorded' : `${formatPoolValue(m.volumeMl)} mL`,
    tnc: formatPoolValue(m.tncTotal),
    cd34: formatPoolValue(m.cd34Total),
    cd3: formatPoolValue(m.cd3Total),
    // CD34 and CD3 resolve independently, so a member can be single platform
    // for one and dual for the other. Name both only when they differ.
    rule:
      m.cd34Rule === m.cd3Rule
        ? doseRuleLabel(m.cd34Rule)
        : `CD34 ${doseRuleLabel(m.cd34Rule).toLowerCase()}, CD3 ${doseRuleLabel(m.cd3Rule).toLowerCase()}`,
  }))
}

/** The sentence under the per-kg rows when no recipient weight is on file. */
export function missingWeightMessage(missing: LimsResultRef[]): string | null {
  if (missing.length === 0) return null
  const names = missing.map(limsLabelFor).join(', ')
  return `No recipient weight is on file for this patient, so ${names} cannot be calculated. They are left absent rather than recorded as zero.`
}
